import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bindAll } from 'bind-event-listener';
import type { UnbindFn } from 'bind-event-listener';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { INPUT_METHOD, INSERT_MEDIA_VIA } from '@atlaskit/editor-common/analytics';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type {
	_MarkdownModePluginStub,
	OptionalPlugin,
	PublicPluginAPI,
} from '@atlaskit/editor-common/types';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type { MediaPlugin } from '@atlaskit/editor-plugin-media';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles';
import { getMediaClient } from '@atlaskit/media-client-react/get-media-client';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import {
	buildImageUrl,
	getRemixDataConsumerSource,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import {
	ExperienceName as RovoExperienceName,
	remixEditExistingExperience,
	REMIX_EDIT_EXISTING_MARKS,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { fetchMauiPersonalReadToken } from '@atlassian/conversation-assistant-service/common/utils/maui-media-token';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import type { ConfluenceUpdateImageOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import {
	ContentDestroyCommand,
	ContentReadyCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/content-lifecycle-commands';
import type {
	EditorInsertContentCommandProperties,
	EditorInsertContentPresentation,
} from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { sendAIFCExperienceTrackingToParent } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import type {
	OnUploadAndInsertMediaMeta,
	PassThroughCommandProps,
	StreamAbortCallback,
	StreamCallback,
	StreamEndCallback,
	StreamErrorCallback,
	StreamRepairCallback,
	SendContextReadyCallback,
	StreamStartCallback,
	StreamTimeoutCallback,
} from './command-handlers/command-handler';
import { CommandManager } from './command-manager';
import type { AddCommandHandlersFn } from './types/command-manager';
import type { AdditionalContext, ContentLocation } from './types/content';
import type { CreateMediaProviderFn, UploadImageErrorCallback } from './types/media';
import { useMediaImageProcessor } from './useMediaImageProcessor';
import type { MediaNode } from './useMediaImageProcessor';
import { log } from './utils/aifc-logging';
import {
	fireUploadAndInsertMediaErrorAnalyticsEvent,
	fireStaleMediaCollectionAnalyticsEvent,
} from './utils/analytics';

/* eslint-disable @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); preconditioning is intentional and will be removed when cleanup lands */
const isMauiInlineEditLoadingEnabled = (): boolean =>
	fg('cc-maui-phase-2') && expValEquals('cc-maui-experiment', 'isEnabled', true);
/* eslint-enable @atlaskit/platform/no-preconditioning */

/** Designer-tuned container dimensions per aspect ratio for the `ImageFromAIGeneration` insert flow. */
export const IMAGE_FROM_AI_GENERATION_ASPECT_RATIO_DIMENSIONS: Record<
	string,
	{ height: number; width: number }
> = {
	'16:9': { width: 760, height: 424 },
	'3:2': { width: 700, height: 470 },
	'1:1': { width: 592, height: 592 },
	'2:3': { width: 470, height: 701 },
	'9:16': { width: 424, height: 760 },
};

/** Returns sizing for ImageFromAIGeneration inserts; `null` for any other caller so they fall back to `akEditorDefaultLayoutWidth`. */
export const getImageFromAIGenerationDimensions = (
	meta?: OnUploadAndInsertMediaMeta,
): { height: number; width: number } | null => {
	if (meta?.inputMethod !== INPUT_METHOD.REMIX_FROM_MEDIA_INSERTS) {
		return null;
	}
	const aspectRatio = meta?.aspectRatio;
	if (!aspectRatio) {
		return null;
	}
	return IMAGE_FROM_AI_GENERATION_ASPECT_RATIO_DIMENSIONS[aspectRatio] ?? null;
};

/**
 * Checks if the current environment is staging
 */
export const isStaging = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	const host = window.location.host;
	return (
		host.includes('.stg.atlassian') ||
		host.includes('.stg-east.frontend.public.atl-paas.net') ||
		host.includes('jira-dev.com')
	);
};

const bindRovoBridgeTeardown = (teardown: () => void): UnbindFn => {
	const run = () => {
		/**
		 * @description We do NOT want to yield a return value here. It can cause certain unload
		 * handlers to display a window alert with "Are you sure you want to leave this page?".
		 * Specifically, see @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event,
		 * which is remarked to be a cancellable event by browsers.
		 */
		teardown();
		// Although unnecessary (the JS context is being destroyed), we remove the event listeners
		// to avoid any potential memory leaks due to hanging references - side effects can cause this.
		unbind();
	};

	// Below, we attempt to teardown on different event handlers which we know will trigger
	// on complete unmount of the document in this JS context. Note: Same-origin iframes are
	// indeed considered their own JS context here; just that they have access to other contexts.
	// Browsers report `unload` and `beforeunload` to be unreliable. Hence, we leverage a chain
	// of these events. See official MDN documentation for more (linked below).

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
	 * TODO - bring beforeunload back by consolidating handlers per discussion at -
	 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/207747/overview#comment-674441389
	 */
	// window.addEventListener('beforeunload', run, { once: true });

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
	 */
	const unbind = bindAll(window, [
		{
			type: 'unload',
			listener: run,
			options: { once: true },
		},
		{
			type: 'pagehide',
			listener: run,
			options: { once: true },
		},
	]);

	return unbind;
};

function useRovoBridgeLifecycle(
	props: UseEditorRovoBridgeProps & {
		clientId: ClientId;
		onBridgeReady?: () => void;
		rovoBridgeAPI: RovoContentBridgeAPI | null;
	},
) {
	const contentType = 'page';
	const { clientId, contentLocation, contentId, rovoBridgeAPI, onBridgeReady } = props;

	useEffect(() => {
		if (!rovoBridgeAPI) {
			return;
		}
		// When the fix gate is OFF, call ready() here (original behaviour) —
		// AFTER ContentReadyCommand has been delivered synchronously to BridgeAPIHandlers.
		// When the fix gate is ON, ready() is called in the command-registration
		// effect (after addAndRegisterCommands()) so queued commands are only
		// drained once command handlers are ready.
		if (!fg('aifc_fix_reconnect_stream_overlap')) {
			rovoBridgeAPI.ready(clientId);
		}

		rovoBridgeAPI.send(ContentReadyCommand, {
			senderId: clientId,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId,
			contentType,
			canProvideContext: true,
			contentContext: contentLocation,
		});

		if (!fg('aifc_fix_reconnect_stream_overlap')) {
			// Fire after both sides of the bridge are ready and ContentReadyCommand
			// has been delivered synchronously to BridgeAPIHandlers.
			onBridgeReady?.();
		}

		function rovoBridgeTeardown() {
			if (!rovoBridgeAPI) {
				return;
			}

			rovoBridgeAPI.send(ContentDestroyCommand, {
				senderId: clientId,
				receiverId: ROVO_CHAT_CLIENT_ID,
				contentId,
				contentType,
				contentContext: contentLocation,
			});
			rovoBridgeAPI.teardown(clientId);
		}

		const unbind = bindRovoBridgeTeardown(() => rovoBridgeTeardown());

		return () => {
			try {
				unbind();
				rovoBridgeTeardown();
				// TODO: EDITOR-123 - clear inactivity timeouts?
			} catch {
				// TODO: EDITOR-123 - handle errors; should go to Sentry
				// could be sent in future by navigation lockout
			}
		};
	}, [clientId, contentLocation, contentId, contentType, rovoBridgeAPI, onBridgeReady]);
}

export type UseEditorRovoBridgeProps = {
	addCommandHandlers?: AddCommandHandlersFn;
	additionalContext?: AdditionalContext;
	contentId: string;
	contentLocation: ContentLocation;
	createMediaProvider?: CreateMediaProviderFn;
	editorAPI:
		| PublicPluginAPI<
				[
					AnalyticsPlugin,
					AiStreamingOrchestratorPlugin,
					AIPlugin,
					LocalIdPlugin,
					MediaPlugin,
					OptionalPlugin<_MarkdownModePluginStub>,
					OptionalPlugin<MalleableUiPlugin>,
				]
		  >
		| undefined
		| null;
	/**
	 * Whether the content is backed by a real-time collab document. Takes
	 * precedence over deriving the capability from `editorAPI.collabEdit`, which
	 * races the collab plugin's initialization. Callers that statically know the
	 * editor is collab-backed (e.g. the full-page editor) should pass `true`.
	 */
	hasRealTimeCollab?: boolean;
	mediaProvider?: Promise<MediaProvider>;
	/** Called after rovoBridgeAPI.ready() and ContentReadyCommand have been sent,
	 * guaranteeing both sides of the bridge are ready to communicate. */
	onBridgeReady?: () => void;
	onInsertContent?: (payload: EditorInsertContentCommandProperties) => void;
	onSendContextReady?: SendContextReadyCallback;
	onStream?: StreamCallback;
	onStreamAbort?: StreamAbortCallback;
	onStreamEnd?: StreamEndCallback;
	onStreamError?: StreamErrorCallback;
	onStreamRepair?: StreamRepairCallback;
	onStreamStart?: StreamStartCallback;
	onStreamTimeout?: StreamTimeoutCallback;
	onUploadAndInsertMediaError?: (params: { retry: () => Promise<void> }) => void;
	onUploadImageError?: UploadImageErrorCallback;
	/**
	 * When true, the bridge registers even when `editorAPI.aiExperience` is
	 * absent. Used by the AI Planner bridge route where `disableEditorAI`
	 * suppresses the AI UI plugin but streaming must still work. All
	 * `aiExperience` call sites in this hook are already optional-chained or
	 * feature-gated, so this is safe. See CIAP-136.
	 */
	skipAiExperienceRequirement?: boolean;
	useGenericEditorSkill?: boolean;
	userId?: string;
};

/**
 * React hook that manages the Rovo bridge connection for AI streaming functionality in the Atlassian Editor.
 *
 * This hook establishes a bidirectional communication bridge between the editor and Rovo chat,
 * enabling AI-powered content generation, editing, and streaming operations. It handles the complete
 * lifecycle of the bridge connection including setup, command registration, and cleanup.
 *
 * Typically this is required for use with the `aiStreamingOrchestratorPlugin` and the free gen prompt field
 *
 * @param props - Configuration properties for the Rovo bridge
 * @param props.userId - Optional user ID for the user editing the content
 * @param props.contentLocation - The location context of the content ('global' | 'staging-area')
 * @param props.contentId - Unique identifier for the content being edited
 * @param props.editorAPI - The editor's plugin API, must include AnalyticsPlugin and AiStreamingOrchestratorPlugin
 * @param props.useGenericEditorSkill - [Recommended] Optional to enable the generic editor skill for products that don't have a specific minion
 * @param props.additionalContext - Optional additional context to pass to ReadContentCommand
 * @param props.mediaProvider - Optional media provider
 * @param props.onBridgeReady - Optional callback invoked when the bridge is ready
 * @param props.createMediaProvider - Optional function to create a media provider
 * @param props.onStreamStart - Optional callback when AI streaming starts
 * @param props.onStream - Optional callback for each streaming chunk received
 * @param props.onStreamEnd - Optional callback when AI streaming completes successfully
 * @param props.onStreamAbort - Optional callback when AI streaming is aborted
 * @param props.onStreamTimeout - Optional callback when AI streaming times out
 * @param props.onStreamError - Optional callback when AI streaming encounters an error
 * @param props.onStreamRepair - Optional callback for AI stream repair operations
 * @param props.onUploadImageError - Optional callback to handle upload image errors
 * @param props.onUploadAndInsertMediaError - Optional callback to handle upload and insert media errors
 * @param props.skipAiExperienceRequirement - Optional flag to register the bridge without the AI experience plugin
 * @param props.onInsertContent - Optional callback to handle insert content
 * @param props.addCommandHandlers - Optional function to add custom command handlers
 *
 * @example
 * ```tsx
 * import { useEditorRovoBridge } from '@atlassian/editor-rovo-bridge';
 *
 * function MyEditor() {
 *   const { preset, editorApi: editorAPI } = usePreset(...);
 *
 *   useEditorRovoBridge({
 *     userId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
 *     contentLocation: 'global',
 *     contentId: 'page-123',
 *     editorAPI,
 *     useGenericEditorSkill: true,
 *     additionalContext: {
 *       isViewMode: true,
 *     },
 *     onStreamStart: ({ streamType, payload }) => {
 *       console.log('AI streaming started:', streamType);
 *     },
 *     onStream: ({ streamType, payload }) => {
 *       console.log('AI streaming data:', payload);
 *     },
 *     onStreamEnd: ({ streamType, adfDoc }) => {
 *       console.log('AI streaming completed with ADF:', adfDoc);
 *     },
 *     onStreamError: ({ error, streamType }) => {
 *       console.error('AI streaming error:', error);
 *     },
 *   });
 *
 *   return <ComposableEditor preset={preset} />;
 * }
 * ```
 */
export const useEditorRovoBridge = ({
	contentLocation,
	contentId,
	userId,
	editorAPI,
	skipAiExperienceRequirement,
	useGenericEditorSkill,
	additionalContext,
	hasRealTimeCollab,
	mediaProvider,
	createMediaProvider,
	onUploadImageError,
	onUploadAndInsertMediaError,
	onStreamStart,
	onStream,
	onStreamEnd,
	onStreamAbort,
	onStreamTimeout,
	onStreamError,
	onStreamRepair,
	onSendContextReady,
	onBridgeReady,
	onInsertContent,
	addCommandHandlers,
}: UseEditorRovoBridgeProps): void => {
	const { propertiesRef } = useAnalyticsPropertiesContext();
	const product = 'confluence';
	const contentType = 'page';
	const clientId = getConfluenceEditorClientId(contentId);
	const contentIdRef = useRef(contentId);
	contentIdRef.current = contentId;

	useEffect(() => {
		if (!editorAPI) {
			return;
		}
		editorAPI.aiExperience?.actions.updateTargetId(contentId);
	}, [editorAPI, contentId]);

	const rovoBridgeAPI = useMemo(() => {
		if (
			!editorAPI ||
			(!skipAiExperienceRequirement && !editorAPI.aiExperience) ||
			!editorAPI.aiStreamingOrchestrator
		) {
			return null;
		}

		return getBrowserBridgeAPI();
	}, [editorAPI, skipAiExperienceRequirement]);

	const imageProcessor = useMediaImageProcessor({
		mediaProvider,
		createMediaProvider,
		onUploadImageError,
	});

	const mediaProviderRef = useRef<Promise<MediaProvider> | undefined>(undefined);
	useEffect(() => {
		mediaProviderRef.current = mediaProvider;
	}, [mediaProvider]);

	// Behind the `cc-maui-overlay-by-localid` experiment the AI-generating overlay
	// is driven solely by the inline-Rovo streaming target (a per-node localId
	// staged at Ask Rovo / Edit image submit, cleared when the session ends,
	// errors, or is cancelled). In that mode we skip the ACTION_PREPARE overlay
	// path below, because its payload only carries a media file id which is shared
	// by copy/pasted duplicates and would light up every copy. When the experiment
	// is off, these callbacks keep the previous media-file-id behaviour.
	const onPrepareImageUpdate: PassThroughCommandProps['onPrepareImageUpdate'] = useCallback(
		(previousMediaFileId: string) => {
			if (expValEquals('cc-maui-overlay-by-localid', 'isEnabled', true)) {
				return;
			}
			if (!isMauiInlineEditLoadingEnabled()) {
				return;
			}
			const mediaAPI = editorAPI?.media;
			if (!mediaAPI) {
				return;
			}

			editorAPI.core.actions.execute(mediaAPI.commands.setAIGenerating(previousMediaFileId));
		},
		[editorAPI],
	);

	const onClearAIGenerating: PassThroughCommandProps['onClearAIGenerating'] = useCallback(
		(previousMediaFileId: string) => {
			if (expValEquals('cc-maui-overlay-by-localid', 'isEnabled', true)) {
				return;
			}
			if (!isMauiInlineEditLoadingEnabled()) {
				return;
			}
			const mediaAPI = editorAPI?.media;
			if (!mediaAPI) {
				return;
			}

			editorAPI.core.actions.execute(mediaAPI.commands.clearAIGenerating(previousMediaFileId));
		},
		[editorAPI],
	);

	// Set of file IDs that we've set AI generating decoration on and are waiting
	// for the media card to signal that the image preview has rendered.
	const pendingAIClearIds = useRef<Set<string>>(new Set());

	/**
	 * Tracks an AI-generated media file during the CWR streaming flow.
	 * Sets the AI generating decoration (START). The decoration is cleared (END)
	 * when the media card signals that the image preview has rendered, via the
	 * media plugin's sharedState.onChange subscription below.
	 */
	const onTrackAIGeneratingMedia: PassThroughCommandProps['onTrackAIGeneratingMedia'] = useCallback(
		(fileId: string, _collectionName?: string) => {
			if (!isExperimentEnabled('aifc_page_create_with_rovo_include_infographics')) {
				return;
			}
			const mediaAPI = editorAPI?.media;
			if (!mediaAPI) {
				return;
			}

			editorAPI.core.actions.execute(mediaAPI.commands.setAIGenerating(fileId, 'cwr'));
			pendingAIClearIds.current.add(fileId);
		},
		[editorAPI],
	);

	// Subscribe to the media plugin's shared state to detect when a media card's
	// image preview has rendered. When a file ID we're tracking appears in
	// previewRenderedMediaIds, clear the AI generating decoration.
	usePluginStateEffect(editorAPI, ['media'], ({ mediaState }) => {
		if (!isExperimentEnabled('aifc_page_create_with_rovo_include_infographics')) {
			return;
		}
		if (!mediaState?.previewRenderedMediaIds || pendingAIClearIds.current.size === 0) {
			return;
		}
		for (const fileId of Array.from(pendingAIClearIds.current)) {
			if (mediaState.previewRenderedMediaIds.has(fileId)) {
				if (editorAPI?.media) {
					editorAPI.core.actions.execute(editorAPI.media.commands.clearAIGenerating(fileId));
				}
				pendingAIClearIds.current.delete(fileId);
			}
		}
	});

	// When the AI plugin's `inlineRovoStreamingTargetId` flips on
	// or off (session end / cancel), mirror it onto the media plugin's
	// existing AI-generating decoration. This gives media nodes an
	// optimistic prompt submit-time loading overlay without
	// coupling `editor-plugin-media` to `editor-plugin-ai`.
	const previousInlineRovoTargetIdRef = useRef<string | null>(null);
	usePluginStateEffect(editorAPI, ['aiExperience'], ({ aiExperienceState }) => {
		if (!isMauiInlineEditLoadingEnabled()) {
			return;
		}

		const mediaCommands = editorAPI?.media?.commands;
		const coreActions = editorAPI?.core?.actions;
		if (!mediaCommands || !coreActions) {
			return;
		}

		const next =
			aiExperienceState?.aiDecorationExperiencePluginState?.inlineRovoStreamingTargetId ?? null;
		const previous = previousInlineRovoTargetIdRef.current;
		if (next === previous) {
			return;
		}
		previousInlineRovoTargetIdRef.current = next;

		if (next != null) {
			coreActions.execute(mediaCommands.setAIGenerating(next));
		} else if (previous != null) {
			coreActions.execute(mediaCommands.clearAIGenerating(previous));
		}
	});

	const onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia'] = useCallback(
		async (
			sourceUrl: string,
			payload?: Partial<ConfluenceUpdateImageOnPageData>,
			_presentation?: EditorInsertContentPresentation,
			meta?: OnUploadAndInsertMediaMeta,
		) => {
			if (!editorAPI || !editorAPI.media) {
				log('onUploadAndInsertMedia: Editor API or media plugin not available');
				return;
			}
			const resolvedProvider = await mediaProviderRef.current;
			if (
				!resolvedProvider?.uploadMediaClientConfig ||
				!resolvedProvider.uploadParams?.collection
			) {
				log('onUploadAndInsertMedia: No media provider available');
				return;
			}

			const { mediaFileId, previousMediaFileId, dynamicUiSource, dynamicUiSubtype } = payload ?? {};
			const localId = dynamicUiSource?.startLocalId;

			try {
				const mediaClient = getMediaClient(resolvedProvider.uploadMediaClientConfig);
				const targetCollection = resolvedProvider.uploadParams.collection;

				// Detect stale collection: if the frozen uploadParams.collection doesn't match
				// contentId-${contentId}, the MediaProvider was created for a different page
				// IMPORTANT!!! This only works for Confluence and is designed to be temporary.
				// Other products behave differently
				if (fg('platform_editor_ai_stale_media_collection_analytic')) {
					const expectedCollection = `contentId-${contentIdRef.current}`;
					if (targetCollection !== expectedCollection) {
						fireStaleMediaCollectionAnalyticsEvent(
							editorAPI,
							targetCollection,
							contentIdRef.current,
						);
					}
				}

				if (previousMediaFileId && mediaFileId && userId) {
					const { token, collection } = await fetchMauiPersonalReadToken(product);
					const imageUrl = buildImageUrl({
						fileId: mediaFileId,
						token: token.split('Bearer ')[1],
						collection,
						clientId: userId,
						isStaging: isStaging(),
					});
					const result = await mediaClient.file.uploadExternal(
						imageUrl,
						targetCollection,
						undefined,
						true,
					);
					const { id, occurrenceKey } = result.uploadableFileUpfrontIds;
					const { height, width } = result.dimensions;

					const aiGeneratedDimensions = getImageFromAIGenerationDimensions(meta);
					const mediaAttrs = {
						id,
						collection: targetCollection,
						occurrenceKey,
						type: 'file' as const,
						__fileMimeType: result.mimeType,
						width: aiGeneratedDimensions?.width ?? akEditorDefaultLayoutWidth,
						height:
							aiGeneratedDimensions?.height ??
							Math.round(akEditorDefaultLayoutWidth * (height / width)),
					};

					// Replace existing media node and set AI generating decoration on the new node
					editorAPI.core.actions.execute(({ tr }) => {
						// Get the editor's schema
						const schema: Schema | undefined = editorAPI.core.sharedState.currentState()?.schema;
						if (!schema) {
							throw new Error('Could not get editor schema for replacing media node');
						}

						const { selection } = tr;
						let found = false;

						// Fast path: if the current selection is a NodeSelection on a mediaSingle
						// that contains the target media node, use that position directly.
						if (selection instanceof NodeSelection) {
							const selectedNode = selection.node;
							if (selectedNode.type === schema.nodes.mediaSingle) {
								const mediaNode = selectedNode.content.child(0);
								if (
									mediaNode &&
									mediaNode.attrs.id === previousMediaFileId &&
									(localId ? mediaNode.attrs.localId === localId : true)
								) {
									tr.setNodeMarkup(selection.from + 1, undefined, {
										...mediaNode.attrs,
										...mediaAttrs,
									});
									found = true;
								}
							}
						}

						// Slow path: traverse the whole document
						let foundMediaParentPos: number | undefined;
						if (!found) {
							tr.doc.descendants((node, pos, parent) => {
								if (
									!found &&
									node.type === schema.nodes.media &&
									node.attrs.id === previousMediaFileId &&
									(localId ? parent?.attrs.localId === localId : true)
								) {
									tr.setNodeMarkup(pos, undefined, {
										...node.attrs,
										...mediaAttrs,
									});
									found = true;
									// media is the first child of mediaSingle, so parent pos = pos - 1
									if (parent?.type === schema.nodes.mediaSingle) {
										foundMediaParentPos = pos - 1;
									}
									return false;
								}
							});
						}

						if (found) {
							const aiDecorationExperiencePluginState =
								editorAPI.aiExperience?.sharedState.currentState()
									?.aiDecorationExperiencePluginState;
							const shouldSuppressScrollIntoView =
								localId !== undefined &&
								aiDecorationExperiencePluginState?.isInlineEditReady === true &&
								aiDecorationExperiencePluginState.inlineEditReadyTargetId === localId &&
								isExperimentEnabled('cc_maui_polish_changes_batch_6');
							// Restore NodeSelection on the edited mediaSingle node
							if (fg('platform_remix_fix_node_replacement_selection')) {
								const selectPos =
									selection instanceof NodeSelection ? selection.from : foundMediaParentPos;
								if (selectPos !== undefined) {
									try {
										tr.setSelection(NodeSelection.create(tr.doc, selectPos));
									} catch {
										// If NodeSelection creation fails, leave selection as-is
									}
								}
							}
							// InlineEditReadyBar owns navigation to an out-of-view result. Explicitly opt
							// out because scrollIntoViewPlugin can scroll any doc-changing transaction.
							if (shouldSuppressScrollIntoView) {
								tr.setMeta('scrollIntoView', false);
							} else {
								tr.scrollIntoView();
							}
							// Mark insertion complete and succeed the remix-edit-existing UFO experience.
							remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.NODE_INSERTED);
							remixEditExistingExperience.success();
						} else {
							remixEditExistingExperience.failure({ metadata: { reason: 'media_node_not_found' } });
						}

						return tr;
					});

					// Set AI generating decoration on the new media node
					if (editorAPI.media && isMauiInlineEditLoadingEnabled() && meta?.isAIGenerated === true) {
						editorAPI.core.actions.execute(editorAPI.media.commands.setAIGenerating(id));
					}

					// Clear AI generating decoration after the upload completes
					const subscription = mediaClient.file.getFileState(id).subscribe({
						next: (fileState) => {
							if (fileState.status !== 'uploading') {
								if (
									editorAPI.media &&
									isMauiInlineEditLoadingEnabled() &&
									meta?.isAIGenerated === true
								) {
									editorAPI.core.actions.execute(editorAPI.media.commands.clearAIGenerating(id));
								}
								subscription.unsubscribe();
							}
						},
						error: (error) => {
							if (editorAPI.media && isMauiInlineEditLoadingEnabled()) {
								editorAPI.core.actions.execute(editorAPI.media.commands.clearAIGenerating(id));
							}
							const errorMessage =
								error instanceof Error
									? error.message
									: typeof error === 'object' && error !== null
										? JSON.stringify(error)
										: String(error);
							const conversationChannelId =
								meta?.conversationChannelId ||
								propertiesRef.current.conversationChannelId ||
								undefined;
							fireUploadAndInsertMediaErrorAnalyticsEvent({
								editorApi: editorAPI,
								errorMessage,
								conversationChannelId,
								errorFlagShown: true,
							});
							onUploadAndInsertMediaError?.({
								retry: async () => {
									await onUploadAndInsertMedia?.(sourceUrl, payload, _presentation, meta);
								},
							});
							subscription.unsubscribe();
						},
					});

					return fg('platform_editor_ai_remix_toolbar') ||
						isExperimentEnabled('platform_editor_maui_delay_actioned_event')
						? ({
								type: 'update',
								fieldId: id,
							} as const)
						: undefined;
				} else {
					const result = await mediaClient.file.uploadExternal(
						sourceUrl,
						targetCollection,
						undefined,
						true,
					);
					const { id, occurrenceKey } = result.uploadableFileUpfrontIds;
					const { height, width } = result.dimensions;

					const aiGeneratedDimensions = getImageFromAIGenerationDimensions(meta);
					const mediaAttrs = {
						id,
						collection: targetCollection,
						occurrenceKey,
						type: 'file' as const,
						__fileMimeType: result.mimeType,
						width: aiGeneratedDimensions?.width ?? akEditorDefaultLayoutWidth,
						height:
							aiGeneratedDimensions?.height ??
							Math.round(akEditorDefaultLayoutWidth * (height / width)),
					};

					// Insert new media node
					if (!editorAPI.media) {
						return;
					}

					const dataConsumerSource = getRemixDataConsumerSource(dynamicUiSubtype);

					const insertMediaSingleCommand = editorAPI.media.commands.insertMediaSingle(
						mediaAttrs,
						meta?.inputMethod ?? INPUT_METHOD.REMIX,
						INSERT_MEDIA_VIA.LOCAL_UPLOAD,
						meta?.positions,
						dataConsumerSource,
					);

					// While a remix "ready" bar is up, that bar owns navigation to the result: the upload
					// lands after the remix completed, so scrolling here would override the position the
					// user picked while it generated and leave the image only partly in view. The bar's
					// "View" action scrolls to the media node instead.
					// State check first so the experiment exposure only fires for users who actually reach
					// this fork (a remix image insert landing while the ready bar is up).
					const isRemixReadyBarOwningScroll =
						editorAPI.malleableUi?.sharedState.currentState()?.isComplete === true &&
						expValEquals('cc_maui_polish_changes_batch_4', 'isEnabled', true);

					editorAPI.core.actions.execute(({ tr }) => {
						const insertTr = insertMediaSingleCommand({ tr });
						if (!insertTr) {
							return tr;
						}
						// Dropping `scrollIntoView()` is not enough: `scrollIntoViewPlugin` appends a scroll
						// for any doc-changing transaction, so the insert has to opt out explicitly (the
						// same `scrollIntoView: false` meta the media plugin's own commands set).
						return isRemixReadyBarOwningScroll
							? insertTr.setMeta('scrollIntoView', false)
							: insertTr.scrollIntoView();
					});

					// Mark image node insertion complete and succeed the remix-edit-existing
					remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.NODE_INSERTED);
					remixEditExistingExperience.success();

					// Set AI generating decoration on the newly inserted node
					if (editorAPI.media && isMauiInlineEditLoadingEnabled() && meta?.isAIGenerated === true) {
						editorAPI.core.actions.execute(editorAPI.media.commands.setAIGenerating(id));
					}

					// Clear AI generating decoration after the upload completes
					const subscription = mediaClient.file.getFileState(id).subscribe({
						next: (fileState) => {
							if (fileState.status !== 'uploading') {
								if (
									editorAPI.media &&
									isMauiInlineEditLoadingEnabled() &&
									meta?.isAIGenerated === true
								) {
									editorAPI.core.actions.execute(editorAPI.media.commands.clearAIGenerating(id));
								}
								subscription.unsubscribe();
							}
						},
						error: (error) => {
							if (editorAPI.media && isMauiInlineEditLoadingEnabled()) {
								editorAPI.core.actions.execute(editorAPI.media.commands.clearAIGenerating(id));
							}
							const errorMessage =
								error instanceof Error
									? error.message
									: typeof error === 'object' && error !== null
										? JSON.stringify(error)
										: String(error);
							const conversationChannelId =
								meta?.conversationChannelId ||
								propertiesRef.current.conversationChannelId ||
								undefined;
							fireUploadAndInsertMediaErrorAnalyticsEvent({
								editorApi: editorAPI,
								errorMessage,
								conversationChannelId,
								errorFlagShown: true,
							});
							onUploadAndInsertMediaError?.({
								retry: async () => {
									await onUploadAndInsertMedia?.(sourceUrl, payload, _presentation, meta);
								},
							});
							subscription.unsubscribe();
						},
					});

					return fg('platform_editor_ai_remix_toolbar') ||
						isExperimentEnabled('platform_editor_maui_delay_actioned_event')
						? ({
								type: 'create',
								fieldId: id,
							} as const)
						: undefined;
				}
			} catch (error) {
				log('onUploadAndInsertMedia: Error uploading media', { error });
				const errorMessage =
					error instanceof Error
						? error.message
						: typeof error === 'object' && error !== null
							? JSON.stringify(error)
							: String(error);
				const conversationChannelId =
					meta?.conversationChannelId || propertiesRef.current.conversationChannelId || undefined;
				fireUploadAndInsertMediaErrorAnalyticsEvent({
					editorApi: editorAPI,
					errorMessage,
					conversationChannelId,
				});
			} finally {
				editorAPI.aiExperience?.actions.endAIExperience();
			}
		},
		// propertiesRef is a ref; reading propertiesRef.current.conversationChannelId at call time
		// is intentional and must not be a dep (would defeat the ref pattern).
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[editorAPI, userId],
	);

	const reuploadImages: PassThroughCommandProps['reuploadImages'] = useCallback(
		async (adfDoc?: DocNode) => {
			const processImagesAndUpdateDocument = async () => {
				if (!imageProcessor?.processImagesInAdf) {
					throw new Error(
						'Image processor not available for image re-uploading in editor streaming',
					);
				}
				if (!editorAPI) {
					throw new Error('Editor API not available for image re-uploading in editor streaming');
				}

				if (fg('confluence_frontend_aifc_reupload_image')) {
					const editorDoc = await editorAPI.core.actions.requestDocumentAsync();
					if (!editorDoc) {
						throw new Error('Could not get document from editor for image re-uploading');
					}

					const { error, hasChanges, changedNodesByLocalId } =
						await imageProcessor.processImagesInAdf(editorDoc as DocNode);

					log('reuploadImages', { error, hasChanges, adfDoc, changedNodesByLocalId });

					if (error) {
						throw error;
					}

					if (hasChanges && changedNodesByLocalId && changedNodesByLocalId.size > 0) {
						// Get the editor's schema to convert ADF to ProseMirror nodes
						const schema: Schema | undefined = editorAPI.core.sharedState.currentState()?.schema;
						if (!schema) {
							throw new Error('Could not get editor schema for image re-uploading');
						}
						// Replace individual nodes
						changedNodesByLocalId.forEach((adfNode: MediaNode, localId: string) => {
							// Convert ADF to ProseMirror node
							const pmNode = schema.nodeFromJSON(adfNode);
							editorAPI?.localId?.actions.replaceNode({
								localId,
								value: pmNode,
							});
						});
					}
				} else {
					if (!adfDoc) {
						throw new Error('adfDoc is required for image re-uploading when feature flag is off');
					}
					const { error, hasChanges, processedAdf } =
						await imageProcessor.processImagesInAdf(adfDoc);

					log('reuploadImages', { error, hasChanges, processedAdf, adfDoc });

					if (error) {
						throw error;
					}

					if (hasChanges) {
						editorAPI?.core.actions.replaceDocument(processedAdf, {
							scrollIntoView: false,
						});
					}
				}
			};

			try {
				await processImagesAndUpdateDocument();
			} catch (error) {
				void sendAIFCExperienceTrackingToParent({
					status: 'fail',
					actionType: 'page',
					actionId: contentId,
					contentContext: contentLocation,
					experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
					error: (error as Error).message,
				});

				onUploadImageError?.({ error: error as Error });
			}
		},
		[imageProcessor, editorAPI, contentId, contentLocation, onUploadImageError],
	);

	const commandProps = useMemo<PassThroughCommandProps | null>(() => {
		if (!editorAPI) {
			return null;
		}
		return {
			clientId,
			contentLocation,
			contentId,
			contentType,
			editorAPI,
			useGenericEditorSkill,
			additionalContext,
			hasRealTimeCollab,
			onStreamStart,
			onStream,
			onStreamEnd,
			onStreamAbort,
			onStreamTimeout,
			onStreamError,
			onStreamRepair,
			onSendContextReady,
			onInsertContent,
			onPrepareImageUpdate,
			onClearAIGenerating,
			onTrackAIGeneratingMedia,
			onUploadAndInsertMedia,
			reuploadImages,
		};
	}, [
		clientId,
		contentLocation,
		contentId,
		contentType,
		editorAPI,
		useGenericEditorSkill,
		additionalContext,
		hasRealTimeCollab,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		onSendContextReady,
		onInsertContent,
		onPrepareImageUpdate,
		onClearAIGenerating,
		onTrackAIGeneratingMedia,
		onUploadAndInsertMedia,
		reuploadImages,
	]);

	const commandManagerRef = useRef<CommandManager | null>(null);

	useEffect(() => {
		if (!editorAPI || !rovoBridgeAPI || !commandProps) {
			return;
		}

		// only initialize command manager once because
		// createCommandProcessor is stateful and doesn't support re-creation for now
		if (!commandManagerRef.current) {
			commandManagerRef.current = new CommandManager(
				clientId,
				rovoBridgeAPI,
				commandProps,
				addCommandHandlers,
			);
			commandManagerRef.current.addAndRegisterCommands();

			// When the fix gate is ON, call ready() here AFTER command handlers
			// are registered so that bridge-queued commands (e.g.
			// ConfluencePageCreateCommand buffered during AIFC reconnection) are
			// only drained once the editor has handlers ready to process them.
			// When the gate is OFF, ready() is called in useRovoBridgeLifecycle
			// (original behaviour, before editorAPI was available).
			if (fg('aifc_fix_reconnect_stream_overlap')) {
				rovoBridgeAPI.ready(clientId);

				// Fire onBridgeReady AFTER ready() so callers know the full bridge
				// handshake (handlers registered + receiver announced) is complete.
				onBridgeReady?.();
			}
		}
	}, [editorAPI, clientId, rovoBridgeAPI, commandProps, addCommandHandlers, onBridgeReady]);

	// clientId shouldn't change, but just in case
	// when it changes, we re-register the commands to the updated clientId
	useEffect(() => {
		if (!commandManagerRef.current || !rovoBridgeAPI) {
			return;
		}
		commandManagerRef.current.updateClientId(clientId, rovoBridgeAPI);
	}, [clientId, rovoBridgeAPI]);

	// when commandProps changes, we update the command props
	useEffect(() => {
		if (!commandManagerRef.current || !commandProps) {
			return;
		}
		commandManagerRef.current.updateCommandProps(commandProps);
	}, [commandProps]);

	// when addCommandHandlers changes, we deregister and register the commands
	useEffect(() => {
		if (!commandManagerRef.current) {
			return;
		}

		commandManagerRef.current.updateAddCommandHandlers(addCommandHandlers);
	}, [addCommandHandlers]);

	// When enabled, also run this cleanup on bridge client changes so page transitions create
	// a fresh command manager for the next client.
	useEffect(() => {
		return () => {
			commandManagerRef.current?.deregisterCommands();

			if (fg('aifc_reset_command_manager_on_transition')) {
				commandManagerRef.current = null;
			}
		};
	}, [clientId, rovoBridgeAPI]);

	useRovoBridgeLifecycle({
		contentLocation,
		contentId,
		editorAPI,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		addCommandHandlers,
		clientId,
		rovoBridgeAPI,
		onBridgeReady,
	});
};
